import React, { useState, useEffect, useMemo } from "react";
import { LIST_CATER_CASH } from "../../../Graphql/queries";
import { Table, TableHead, TableCell, TableRow, IconButton, Tooltip, Checkbox, FormControlLabel } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import { useLazyQuery } from "@apollo/client/react/hooks";
import TableContainer from "@mui/material/TableContainer";
import { userDeatils } from "../../../ReactiveVariables/index";
import { useReactiveVar } from "@apollo/client";
import { GET_ONE_ORDER, PRINT_INVOICE } from "../../../Graphql/queries";
import { OrderInvoice } from "./order-invoice/order-invoice";
import { useAppDispatch } from "../../../services/redux";
import { clearEditOrder } from "../../../services/redux/order";
import { OrderItem } from "./order-item";
import { IOrderResponseItem } from "../order.typings";
import { useOrderListState } from "./order-list.state";
import { OrderListPagination } from "./order-list-pagination";
import { getUserId } from "@utils/user-format.utils";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { getOrderStatusColor } from "@utils/order-format.utils";

interface IOrderListProps {
  tile?: string;
  addNewOrder?: boolean;
  completed?: boolean;
  catererId: string;
  customerId: string;
}

// Define sortable columns
const SORTABLE_COLUMNS = {
  orderNo: "Orders",
  hasDriver: "Driver Set",
  orderDate: "Created",
  deliveryDate: "Event Date & Time",
  locationName: "Store Name & Delivery Address",
  customerName: "Customer",
  grandTotal: "Order Total",
  orderStatus: "Status",
  invoice: "Invoice",
  action: "Action",
  caterCash: "CaterCash"
};

export function OrderList(props: IOrderListProps) {
  const userAgent = navigator.userAgent;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [openInvoice, setOpenInvoice] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IOrderResponseItem>(null);
  const [invoiceOrderId, setInvoiceOrderID] = useState("");
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const user = useReactiveVar(userDeatils);

  const [printOrderInvoices, { data: printInvoice, loading }] = useLazyQuery(PRINT_INVOICE, {
    variables: { orderId: invoiceOrderId, timeZone: timeZone },
    onCompleted: (res) => {
      setInvoiceUrl(res.printOrderInvoice.pdfUrl);
    },
  });

  const dispatch = useAppDispatch();

  const [invoiceOrder, { data: orderValues, loading: dataLoading }] = useLazyQuery(
    GET_ONE_ORDER,
    {
      variables: {
        customerId:
          user?.data?.currentUserDetails?._id ||
          user?.data?.createUserIdentity?._id ||
          user?.data?.login?._id,
        orderId: invoiceOrderId,
      },
      onCompleted: (res) => {},
    }
  );

  const [fetchCaterCash, { data: caterCashData }] = useLazyQuery(LIST_CATER_CASH);

  const userType =
    user?.data?.login?.userType ||
    user?.data?.currentUserDetails?.userType ||
    user?.data?.createUserIdentity?.userType ||
    user?.data?.createCaterer?.userType;

  const {
    page,
    rowsPerPage,
    isLoading,
    searchValue,
    orders,
    ordersCount,
    sortField,
    sortOrder,
    groupOnly,
    setGroupOnly,
    setSearchValue,
    setPage,
    setRowsPerPage,
    setSortField,
    setSortOrder,
  } = useOrderListState({
    userType,
    catererId: props.catererId,
    customerId: props.customerId,
    isCompleted: props.completed,
  });

  const userId = getUserId(user);

  const status = {
    status: "completed",
    title: props?.tile,
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle sort order if same field is clicked
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to descending for dates, ascending for others
      setSortField(field);
      const isDateField = ["orderDate", "deliveryDate", "dateCompleted", "createdOn", "updatedOn"].includes(field);
      setSortOrder(isDateField ? "desc" : "asc");
    }
    setPage(1); // Reset to first page when sorting changes
  };

  // Get sort icon for column
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon fontSize="small" style={{ opacity: 0.3 }} />;
    }
    return sortOrder === "asc" ? 
      <ArrowUpwardIcon fontSize="small" /> : 
      <ArrowDownwardIcon fontSize="small" />;
  };

  // Get sortable column label
  const getColumnLabel = (field: string) => {
    return SORTABLE_COLUMNS[field] || field;
  };

  useEffect(() => {
    dispatch(clearEditOrder());
  }, []);

  const orderViewState = {
    openInvoice,
    setOpenInvoice,
    selectedItem,
    setSelectedItem,
    invoiceOrderId,
    setInvoiceOrderID,
    invoiceUrl,
    setInvoiceUrl,
    printOrderInvoices,
    printInvoice,
    loading,
    invoiceOrder,
    orderValues,
    dataLoading,
  };

  const onInvoiceClick = (orderId: string) => {
    setInvoiceOrderID(orderId);
    setOpenInvoice(true);
    printOrderInvoices();
    invoiceOrder();
  };

  // Create caterCash map for admin users
  const caterCashMap = useMemo(() => {
    if (userType !== "Admin" || !caterCashData?.listCaterCash?.data || !orders.length) {
      return {};
    }

    // Get all order IDs from the current orders list
    const orderIds = new Set(orders.map(order => order._id).filter(Boolean));

    return caterCashData.listCaterCash.data.reduce((acc, item) => {
      // Only include catercash records that match orders in the current list
      if (item.orderId && orderIds.has(item.orderId)) {
        // Check if catercash is distributed (status = true) or pending (status = false)
        acc[item.orderId] = item.caterCashStatus === true;
      }
      return acc;
    }, {});
  }, [caterCashData, userType, orders]);

  // Fetch caterCash data for admin users
  useEffect(() => {
    if (userType === "Admin" && orders.length > 0) {
      // Get all order IDs from the current orders list
      const orderIds = orders.map(order => order._id).filter(Boolean);
      
      if (orderIds.length > 0) {
        fetchCaterCash({
          variables: {
            // Remove orderId filter to get all catercash records
            limit: 1000, // Increase limit to get more records
            skip: 0,
          },
        });
      }
    }
  }, [orders, userType]);

  return (
    <div className="order-list-container">
      <div className="order-list-content">
        <div className="order-list-header">
          <div className="order-list-title">
            <h4>{props.tile || "Orders"}</h4>
          </div>
          <div className="order-list-actions">
            {userType !== "Customer" && (
              <div className="order-list-search">
                <input
                  placeholder="Search by Customer Name/OrderId"
                  className="form-control order-search-input"
                  value={searchValue}
                  onChange={(e) => {
                    setPage(1);
                    setSearchValue(e.target.value);
                  }}
                />
              </div>
            )}
            {userType === "Admin" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupOnly}
                    onChange={(e) => {
                      setPage(1);
                      setGroupOnly(e.target.checked);
                    }}
                  />
                }
                label="Admin orders only"
              />
            )}
            </div>
        </div>

        <div className="order-list-table">
          <TableContainer>
            <Table className="commonTabel" sx={{ minWidth: 650 }} aria-label="orders table">
              <TableHead>
                <TableRow>
                  <TableCell onClick={() => handleSort("orderNo")} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {getColumnLabel("orderNo")} {getSortIcon("orderNo")}
                    </div>
                  </TableCell>
                  <TableCell onClick={() => handleSort("orderDate")} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {getColumnLabel("orderDate")} {getSortIcon("orderDate")}
                    </div>
                  </TableCell>
                  <TableCell onClick={() => handleSort("hasDriver")} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {getColumnLabel("hasDriver")} {getSortIcon("hasDriver")}
                    </div>
                  </TableCell>
                  <TableCell onClick={() => handleSort("deliveryDate")} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {getColumnLabel("deliveryDate")} {getSortIcon("deliveryDate")}
                    </div>
                  </TableCell>
                  <TableCell>
                    {getColumnLabel("locationName")}
                  </TableCell>
                  {/* Only show Customer column for Admin and Caterer */}
                  {userType !== "Customer" && (
                    <TableCell>
                      {getColumnLabel("customerName")}
                    </TableCell>
                  )}
                  <TableCell>
                    {getColumnLabel("grandTotal")}
                  </TableCell>
                  <TableCell>
                    {getColumnLabel("orderStatus")}
                  </TableCell>
                  <TableCell>
                    Payment Method
                  </TableCell>
                  <TableCell>
                    {getColumnLabel("invoice")}
                  </TableCell>
                  <TableCell>
                    {getColumnLabel("action")}
                  </TableCell>
                  {userType === "Admin" && (
                    <TableCell>
                      {getColumnLabel("caterCash")}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={userType === "Admin" ? (userType !== "Customer" ? 13 : 12) : (userType !== "Customer" ? 12 : 11)}>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <p> Loading...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : orders.length > 0 ? (
                  orders.map((item) => (
                    <OrderItem
                      key={item?._id}
                      item={item}
                      userType={userType}
                      userId={userId}
                      orderState={orderViewState}
                      onInvoiceClick={onInvoiceClick}
                      hasCaterCash={userType === "Admin" ? caterCashMap[item._id] : undefined}
                      showCustomerColumn={userType !== "Customer"}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={userType === "Admin" ? (userType !== "Customer" ? 13 : 12) : (userType !== "Customer" ? 12 : 11)}>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <p>{searchValue ? "No data Found" : "No list added Yet!"} </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <OrderListPagination
          page={page}
          rowsPerPage={rowsPerPage}
          ordersCount={ordersCount}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </div>
      
      {/* Invoice Modal */}
      {openInvoice && (
        <OrderInvoice
          open={openInvoice}
          setOpen={setOpenInvoice}
          orderId={invoiceOrderId}
          order={orderValues?.getOneOrder}
          invoiceUrl={invoiceUrl}
          loading={loading}
          dataLoading={dataLoading}
        />
      )}
    </div>
  );
}
